/**
 * `homePath` is required because `/` is not guaranteed to exist — the route
 * table is per-role and a dashboard-less user has no `/` route, so a hardcoded
 * "Go Back Home" would bounce them straight back to this page. Callers resolve
 * it with `resolveHomePath(tabs)`.
 */
export default function NotFoundPage({ homePath = "/" }: { homePath?: string }) {
  // Define styles using your CSS variables
  const containerBgStyle = { backgroundColor: "var(--color-background)" }; // #e7ebea
  const primaryTextColor = { color: "var(--color-black-9)" }; // #1e302a
  const accentTextColor = { color: "var(--color-black-6)" }; // #697571
  const buttonBgStyle = {
    backgroundColor: "var(--color-primary)", // #218760
    color: "var(--color-white)",
    borderRadius: "var(--btn-radius)",
  };
  const errorNumberStyle = { color: "var(--color-black-9)" }; // #c1e1d5 (Subtle, large code)

  return (
    // 1. Full screen container: Centered content with your custom background
    <div
      className="flex flex-col items-center justify-center min-h-screen p-4"
      style={containerBgStyle}
    >
      {/* Container for the error content */}
      <div className="text-center max-w-lg">
        {/* 2. Large Error Code (Attention-grabbing but subtle) */}
        <h1
          className="text-9xl font-extrabold opacity-75"
          style={errorNumberStyle}
        >
          404
        </h1>

        {/* 3. Main Message */}
        <h2
          className="text-4xl font-bold mt-4 mb-2 tracking-tight"
          style={primaryTextColor}
        >
          Oops! Page Not Found
        </h2>

        {/* 4. Sub-message */}
        <p className="text-lg mb-8" style={accentTextColor}>
          We can't seem to find the page you're looking for. It might have been
          moved or deleted.
        </p>

        {/* 5. Call-to-Action Button - Styled with your primary color */}
        {/* Note: Tailwind transition/shadow is kept. Only color is variable-driven. */}
        <a
          href={homePath}
          className="
            inline-block 
            px-8 py-3 
            text-lg font-semibold 
            shadow-lg 
            transition duration-300 
            transform hover:scale-[1.03]
            focus:outline-none focus:ring-4 focus:ring-opacity-50
          "
          style={buttonBgStyle} // Applies background, text color, and border radius
        >
          Go Back Home
        </a>
      </div>
    </div>
  );
}
